// Arm a thing, and then the board lights.
//
// Every one of these puts a *mode* up rather than firing: the square is
// chosen on the board, which is the standing rule here (see `Forging` and
// `Aiming`). The state they arm is the session's, because the click handler
// reads all three of them. `useArming` as a set of verbs over `SessionCore`;
// same decisions, same sentences, same command shapes.

import { type PieceType } from "src/engine/rules/board";
import { CARD_TABLE } from "src/engine/rules/campaign/cards";
import { artifactBill, canPayArtifact, uniquePlaceError } from "src/engine/rules/campaign/artifacts";
import { materialName } from "src/engine/rules/materials";
import { sacrificeBlocked, sacrificeTargets } from "src/engine/rules/campaign/sacrifice";
import { isGameOver } from "src/engine/rules/chess/status";
import { holdsGround } from "src/engine/rules/industry/belts";
import { actionRefusal, forgeRefusal } from "src/engine/rules/clock";
import { canTakeVariant } from "src/engine/rules/industry/upgrades";
import { FUSE_ERROR_REFUSAL, fuseError } from "src/engine/rules/campaign/fusion";
import { type StructureKind } from "src/engine/rules/industry/state";
import {
  demolishers,
  isBare,
  reservedSquares,
  STRUCTURE_NAMES
} from "src/engine/rules/industry/structures";

import type { SessionCore } from "./core";
import { sourcesFor } from "./marks";
import { CARD_REFUSAL, REFUSAL, REWIND_REFUSAL, SACRIFICE_SAYS, UPGRADE_REFUSAL } from "./refusals";

/**
 * What a forge tile has to say to be armed: the client's `ForgeTile` fits,
 * and this is the part of it the verb reads. Declared here rather than
 * imported, because the engine does not import the client.
 */
export interface ForgeArm {
  type: PieceType;
  /** Every square a forge of this type could be paid for, over every foundry of the side. */
  squares: number[];
  /** Which foundry leads at each square. */
  leads: ReadonlyMap<number, number>;
  /** How many foundries chip in, at the squares where it is more than one. */
  shared: ReadonlyMap<number, number>;
  /** Null when the tile may be armed; otherwise what the hover card says. */
  reason: string | null;
}

/** `ForgeArm`'s shape for a build tile. */
export interface BuildArm {
  kind: StructureKind;
  squares: number[];
  leads: ReadonlyMap<number, number>;
  reason: string | null;
}

export function armingVerbs(core: SessionCore) {
  /**
   * Arm a forge from the hotbar: light every square any coalition of yours
   * could pay for, and wait for one to be clicked.
   *
   * **It always lights and waits, even with one candidate.** The shelf armed a
   * forge from a machine somebody had already selected, so firing straight
   * down on a lone square was the second half of a gesture that had begun. A
   * global bar is one click away from every other click on the screen, and a
   * mint spends the turn: a bar tile that could dispatch on its own is a
   * misclick that costs a move.
   *
   * The tile carries its own refusal, priced by `hotbarModel` against the
   * whole side rather than against one foundry — which is the thing a
   * per-machine list could not say once the bill was split.
   */
  const armForge = (tile: ForgeArm) => {
    // A second click on the armed tile puts it down, the same way a token
    // chip does — the tool is held, not fired.
    if (core.forging?.type === tile.type) {
      core.forging = undefined;
      core.changed();
      return;
    }
    // Factory Flow first (§31 T4): the tile may predate the move.
    const reason = forgeRefusal(core.state, core.localSide) ?? tile.reason;
    if (reason) {
      core.setRefusal(reason);
      return;
    }
    core.setRefusal(null);
    core.selected = undefined;
    core.pending = undefined;
    // The three aiming modes are exclusive: arming one puts the others down.
    core.placing = undefined;
    core.forging = { type: tile.type, at: tile.squares, leads: tile.leads, shared: tile.shared };
    core.changed();
  };

  /**
   * Buy a piece a variant row where it stands (`upgrade`). Nothing to aim, so
   * nothing to light up. The rank ladder went with the Clone Army (§31 R2), so
   * a call with no variant says so rather than doing nothing.
   */
  const upgradePiece = (square: number, type: PieceType, variant?: number) => {
    const { state, localSide } = core;
    if (!variant) {
      core.setRefusal("That piece cannot climb.");
      return;
    }
    const why = canTakeVariant(state.position, state.industry, square, localSide, variant);
    if (why) {
      core.setRefusal(UPGRADE_REFUSAL[why]);
      return;
    }
    if (!core.send({ type: "upgrade", square, target: type, variant })) {
      core.setRefusal("This army cannot specialise that piece.");
    }
  };

  /** Fuse `parts` into row `into` (§31 P4). Nothing to aim; the rules say why when they refuse. */
  const fusePieces = (parts: readonly number[], into: number) => {
    const { state, localSide } = core;
    const why = fuseError(state, localSide, parts, into);
    if (why) {
      const text = FUSE_ERROR_REFUSAL[why];
      core.setRefusal(`${text[0].toUpperCase()}${text.slice(1)}.`);
      return;
    }
    if (!core.send({ type: "fuse", parts: [...parts], into })) {
      core.setRefusal("Those pieces cannot fuse now.");
    }
  };

  /** Arm a build from the hotbar. `armForge`'s shape, for `armForge`'s reasons. */
  const armBuild = (tile: BuildArm) => {
    if (core.forging?.build === tile.kind) {
      core.forging = undefined;
      core.changed();
      return;
    }
    if (tile.reason) {
      core.setRefusal(tile.reason);
      return;
    }
    core.setRefusal(null);
    core.selected = undefined;
    core.pending = undefined;
    core.placing = undefined;
    core.forging = { build: tile.kind, at: tile.squares, leads: tile.leads };
    core.changed();
  };

  /**
   * Take a building down, with whichever of your pieces is standing beside it.
   *
   * The piece is chosen rather than asked for: cardinals first, so it is the
   * one squarely alongside — and on the usual board there is exactly one. It
   * spends that piece's move, which the shelf says before the click.
   */
  const demolish = (at: number) => {
    const { state, localSide } = core;
    const hands = demolishers(state.position, at, localSide);
    if (hands.length === 0) {
      core.setRefusal("Stand a piece beside it first — taking one down is a move.");
      return;
    }
    if (state.position.turn !== localSide) {
      core.setRefusal("Taking a building down costs a turn, and it is not yours.");
      return;
    }
    core.send({ type: "demolish", square: hands[0], at });
    core.selected = undefined;
    core.changed();
  };

  /**
   * Begin playing a card. Untargeted cards resolve at once; a targeted one
   * lights its candidates and waits for a click.
   */
  const playCard = (instance: number) => {
    const { state } = core;
    if (isGameOver(state.status)) return;
    // **The one command in here that does not go through `send`** is
    // the Time Fluxxer's rewind, which is a log operation like the history preview
    // itself — so the view has to be asked here rather than at the door
    // (§29 U4).
    if (core.viewing) {
      core.setRefusal(REFUSAL["viewing-history"]);
      return;
    }
    core.setRefusal(null);
    const run = state.run;
    const held = run?.deck.find((entry) => entry.id === instance);
    if (!run || !held) return;

    const card = CARD_TABLE[held.card];

    // **The Time Fluxxer never reaches `send`, and cannot.** A rewind is an
    // operation on the *log* — the reducer is handed a state and has never
    // seen a command list — so the store does it, and what lands in the log
    // is a `burn-card` on the board it rewound to. The card's own `refuse`
    // answers `rewind-only` to every play precisely so that dropping this
    // branch fails loudly through the door below, rather than spending the
    // card on a board that does not move.
    if (held.card === "time-fluxxer") {
      const why = core.store.rewindCard(run.side, instance);
      if (why) {
        core.setRefusal(REWIND_REFUSAL[why]);
        return;
      }
      // Spent means the rewind happened; a refused one must stay silent.
      core.cue({ kind: "card-played" });
      core.changed();
      return;
    }

    // **The Spatial Manifold names an edge** (§31 C7): the in-world arrows go
    // up over the board's middle cell and the arrow clicked is the edge.
    if (held.card === "spatial-manifold") {
      const cells = state.position.cellList;
      if (cells.length === 0) return;
      core.selected = undefined;
      core.aiming = undefined;
      core.pending = { kind: "manifold", instance, square: cells[Math.floor(cells.length / 2)] };
      core.changed();
      return;
    }

    if (!card.targeted) {
      if (!core.send({ type: "play-card", instance })) {
        core.setRefusal(CARD_REFUSAL[card.refuse(state, {}) ?? ""] ?? "That card cannot be played.");
      } else core.cue({ kind: "card-played" });
      return;
    }

    // Every square the card would accept. Asking the card itself rather
    // than reimplementing its rule here, so the highlight and the reducer
    // can never disagree about what is a legal target.
    //
    // **A square counts if the card takes it alone *or* takes it paired with
    // a neighbour.** The Hydraulic Clasp wants two squares — the two pieces
    // being clamped — and probing with `{ target }` alone made its
    // `refuse` answer "needs-a-target" for every square on the board, so the
    // card reported that nothing was a legal target and could never be
    // started. Asked this way the flow stays generic: a one-square card is
    // unchanged, since `alone` is true and no pairing is ever looked for.
    const alone = (target: number) => card.refuse(state, { target }) === null;
    // Buildings too, for a state from outside the reducer; §26 C7's bomb destroys one.
    //
    // **Board squares and buildings, and nothing off the frontier.** This
    // also carried the four manifold marks — squares beyond the board's own
    // edge, because §26 C10's only input was a direction and it was picked as
    // a square. That card pushes every edge at once now and is untargeted, so
    // the marks are gone and this is back to the two places a piece or a
    // building can be.
    // A set: a building stands on a cell since §31 S1, so the two overlap.
    const squares = [...new Set([...state.position.cellList, ...state.industry.structures.keys()])];
    const at = squares.filter((square) => alone(square) || sourcesFor(card, state, square).length > 0);
    if (at.length === 0) {
      core.setRefusal("Nothing on the board is a legal target for that card.");
      return;
    }
    core.selected = undefined;
    core.pending = undefined;
    // The card's own voice, lighter: the board is lighting up, not spending
    // anything. Here and nowhere else in the flow — the second-stage clicks
    // are the same aim continuing, and a refusal above never reaches this.
    core.cue({ kind: "card-played" });
    core.aiming = { instance, at };
    core.changed();
  };

  /**
   * Begin a sacrifice. A *power*, so no card and no instance.
   *
   * **The squares come from the rules**, not from a second reading of the six
   * conditions — see `campaign/sacrifice.ts`.
   */
  const beginSacrifice = () => {
    const { state, localSide } = core;
    if (isGameOver(state.status)) return;
    const blocked = sacrificeBlocked(state, localSide);
    if (blocked) {
      core.setRefusal(SACRIFICE_SAYS[blocked]);
      return;
    }
    // A sacrifice is one of the turn's moves (§31 T2, T4): spent or closed by
    // a forge, it is refused here with the reducer's own sentence.
    const closed = state.position.turn === localSide ? actionRefusal(state) : null;
    if (closed) {
      core.setRefusal(closed);
      return;
    }
    const at = sacrificeTargets(state, localSide);
    if (at.length === 0) {
      // Not blocked and nothing to take apart: the king is simply standing
      // alone. Said as its own sentence, or it reads as the power being broken.
      core.setRefusal("Nothing beside your king can be taken apart.");
      return;
    }
    core.setRefusal(null);
    core.selected = undefined;
    core.pending = undefined;
    core.aiming = { instance: -1, at, power: "sacrifice" };
    core.changed();
  };

  /**
   * Place a structure token (§26 F6): pick the token, then pick the square.
   *
   * The targets are the reducer's own tests — bare, and ground you hold — so
   * the board can only offer a square `place-structure` will accept, and a
   * chip that cannot lead anywhere says why instead of arming a mode with
   * nothing lit. Clicking the same chip again puts the tool down.
   */
  const beginPlace = (kind: StructureKind) => {
    const { state } = core;
    if (isGameOver(state.status)) return;
    if (core.placing?.kind === kind) {
      core.placing = undefined;
      core.changed();
      return;
    }
    const run = state.run;
    if (!run || !(run.tokens ?? []).includes(kind)) {
      core.setRefusal(`No ${STRUCTURE_NAMES[kind]} token in hand.`);
      return;
    }
    if (state.position.turn !== run.side) {
      core.setRefusal("Not your turn — placing a structure costs it.");
      return;
    }
    // The Pillar of Excellence: one on the map, and none once one is lost (§31
    // A6); Stoke the Forges: one per side (A10).
    const unique = uniquePlaceError(state, kind, run.side);
    if (unique) {
      core.setRefusal(`Cannot place: ${unique}.`);
      return;
    }
    const reserved = reservedSquares(state);
    const at = state.position.cellList.filter(
      (sq) =>
        isBare(state.position, state.industry, sq, reserved) &&
        holdsGround(state.position, state.industry, sq, run.side)
    );
    if (at.length === 0) {
      core.setRefusal("No bare ground you hold to put it on.");
      return;
    }
    // **Only where the bill can be paid** (§31 A3–A5): the nearest Monarch's
    // inventory then the stores nearest the site, so the square decides.
    const paid = at.filter((sq) => canPayArtifact(state, kind, run.side, sq));
    if (paid.length === 0) {
      const bill = artifactBill(state, kind, run.side)
        .map((row) => `${row.count} ${materialName(row.line, row.grade).toLowerCase()}`)
        .join(" + ");
      core.setRefusal(`Too poor: a ${STRUCTURE_NAMES[kind]} costs ${bill}, from your nearest Monarch then your stores.`);
      return;
    }
    core.setRefusal(null);
    core.selected = undefined;
    core.pending = undefined;
    // The three aiming modes are exclusive: arming one puts the others down.
    core.forging = undefined;
    core.placing = { kind, at: paid };
    core.changed();
  };

  return {
    armForge,
    upgradePiece,
    fusePieces,
    armBuild,
    demolish,
    playCard,
    beginSacrifice,
    beginPlace
  };
}
